import { Box, DialogActions, Divider, Grid, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import { addMessageToRequest, getMessagesFromRequest } from 'lib/api/chat';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import { AuthState } from '../../../../../../services/redux/authSlice';
import { buildRequestId } from '../utils';
import ChatMessage, { Message } from './Components/Message';

const MIN_POLLING_INTERVAL = 10000;

type Props = {
  selectedRequest: object;
  requestType: string;
  setOpenShoppingCart: React.Dispatch<React.SetStateAction<boolean>>;
};

type lastKey = {
  id: string;
  timestamp: string;
};

const ChatWindow = (props: Props) => {
  const { selectedRequest, requestType, setOpenShoppingCart } = props;
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageToSend, setMessageToSend] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [pollingInterval, setPollingInterval] = useState(MIN_POLLING_INTERVAL);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [lastKey, setLastKey] = useState<lastKey>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const dispatch = useDispatch();

  const addTemporalMessage = (text: string) => {
    const role = user?.roles?.[0] ? user?.roles[0].name : '';
    const messageBody = {
      userId: user?.id ?? -1,
      userName: user?.name ?? '',
      userRole: role,
      text,
      timestamp: new Date(),
      loading: true,
    };
    setMessages([...messages, messageBody]);
  };

  const sendMessage = async () => {
    try {
      setMessageToSend('');
      addTemporalMessage(messageToSend);
      await addMessageToRequest(dispatch, {
        requestId: buildRequestId(selectedRequest, requestType),
        requestType,
        message: messageToSend,
      });
      await fetchMessages(false);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMessages = async (canLoad = true, paginationKey?: lastKey) => {
    if (canLoad) setLoading(true);
    if (paginationKey) setLoadingMore(true);

    try {
      const { messages, lastEvaluatedKey } = await getMessagesFromRequest(
        dispatch,
        {
          requestId: buildRequestId(selectedRequest, requestType),
          requestType,
          lastKeyId: paginationKey?.id,
          lastKeyTimestamp: paginationKey?.timestamp,
        },
      );

      setLastKey(lastEvaluatedKey);

      if (paginationKey) {
        setMessages((prev) => [...prev, ...messages]);
        setHasMoreMessages(lastEvaluatedKey !== undefined);
      } else {
        setMessages(messages);
        setHasMoreMessages(lastEvaluatedKey !== undefined);
      }

      if (canLoad) setLoading(false);
      if (paginationKey) setLoadingMore(false);
    } catch (error) {
      console.log(error);
      if (canLoad) setLoading(false);
      if (paginationKey) setLoadingMore(false);
    }
  };

  useEffect(() => {
    void fetchMessages();
  }, []);

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreMessages && lastKey) {
      void fetchMessages(false, lastKey);
    }
  };

  const asyncPolling = async () => {
    await fetchMessages(false);
    if (messages.length === lastMessageCount) {
      setPollingInterval((prevInterval: number) =>
        Math.min(prevInterval * 2, 300000),
      );
    } else {
      setPollingInterval(MIN_POLLING_INTERVAL);
      setLastMessageCount(messages.length);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      void asyncPolling();
    }, pollingInterval);

    return () => clearInterval(intervalId);
  }, [pollingInterval]);

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        maxHeight: '80vh',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column-reverse',
        }}
      >
        {messages.map((message, index) => {
          const currentDate = new Date(message.timestamp).toDateString();
          const nextDate =
            index < messages.length - 1
              ? new Date(messages[index + 1].timestamp).toDateString()
              : null;

          const userId = user?.id ?? -1;
          return (
            <React.Fragment key={index}>
              <ChatMessage
                key={index}
                message={message}
                currentUserId={userId}
              />
              {currentDate !== nextDate && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor: 'grey.200',
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                    }}
                  >
                    {new Date(message.timestamp).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              )}
            </React.Fragment>
          );
        })}
        {messages.length === 0 && (
          <div style={{ padding: '10px 0px' }}>
            <Typography variant="body1">No hay mensajes todavía</Typography>
          </div>
        )}
        {hasMoreMessages && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <CustomButton
              onClick={handleLoadMore}
              disabled={loadingMore}
              color="primary"
              variant="outlined"
              size="small"
            >
              {loadingMore ? 'Cargando...' : 'Cargar más'}
            </CustomButton>
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Grid container style={{ textAlign: 'right', alignItems: 'center' }}>
          <Grid item xs={11}>
            <TextField
              value={messageToSend}
              onChange={(e) => setMessageToSend(e.target.value)}
              variant="outlined"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  void sendMessage();
                  e.preventDefault();
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={1}>
            <CustomButton onClick={sendMessage} color="primary">
              Enviar
            </CustomButton>
          </Grid>
        </Grid>
      </Box>

      <DialogActions>
        <CustomButton
          onClick={() => setOpenShoppingCart(false)}
          color="secondary"
        >
          Salir
        </CustomButton>
      </DialogActions>
    </Box>
  );
};

const MultiChatWindowContainer = (props: any) => {
  const { selectedRequest, requestType, setOpenShoppingCart } = props;

  if (requestType === 'creditLine') {
    return (
      <ChatWindow
        selectedRequest={selectedRequest}
        requestType={requestType}
        setOpenShoppingCart={setOpenShoppingCart}
      />
    );
  }
  if (requestType === 'creditRequest') {
    return (
      <ChatWindow
        selectedRequest={selectedRequest}
        requestType={requestType}
        setOpenShoppingCart={setOpenShoppingCart}
      />
    );
  }
  if (requestType === 'confirming') {
    return (
      <ChatWindow
        selectedRequest={selectedRequest}
        requestType={requestType}
        setOpenShoppingCart={setOpenShoppingCart}
      />
    );
  }

  if (requestType === 'factoring') {
    if (selectedRequest.recipientCompanies.length > 1) {
      return (
        <div>
          {selectedRequest.recipientCompanies.map(
            (company: number, index: number) => {
              return (
                <div key={index}>
                  <Typography variant="h6">
                    {selectedRequest.recipientCompanies[index].name}
                  </Typography>
                  <ChatWindow
                    selectedRequest={{
                      ...selectedRequest,
                      recipientCompany: company,
                    }}
                    requestType={requestType}
                    setOpenShoppingCart={setOpenShoppingCart}
                  />
                  <Divider />
                </div>
              );
            },
          )}
        </div>
      );
    } else if (selectedRequest.recipientCompanies.length === 1) {
      return (
        <ChatWindow
          selectedRequest={{
            ...selectedRequest,
            recipientCompany: selectedRequest.recipientCompanies[0],
          }}
          requestType={requestType}
          setOpenShoppingCart={setOpenShoppingCart}
        />
      );
    }
  }
};

export default MultiChatWindowContainer;
